import React from 'react';
import { apiGet } from '../../api';
import CacEvolutionSection from '../../components/CacEvolutionSection';
import KpiCard from '../../components/KpiCard';
import RetentionSummarySection from '../../components/RetentionSummarySection';
import SimpleBarChart from '../../components/SimpleBarChart';
import StaticKpiCard from '../../components/StaticKpiCard';
import { useInvestorMe } from '../../hooks/useInvestorMe';
import { useAsync } from '../../hooks/useAsync';
import type { KpisResponse } from '../../types';

export default function InvestorKpis() {
  const { me } = useInvestorMe({ enforceNda: true });
  const { data, loading, error } = useAsync<KpisResponse>(() => apiGet('/api/investor/kpis'), []);

  if (loading) return <div className="card">Loading metrics…</div>;
  if (error || !data) return <div className="card">Couldn't load metrics. ({error || 'error'})</div>;

  const k = data.kpis;
  const r25 = data.yearReview?.review2025;
  const y26 = data.yearReview?.live2026;
  const funnelSnapshot = (data.funnel?.snapshot || []).filter((x) => x.stage !== 'Perdido' && x.stage !== 'Ganado');
  const funnelSnapshotDisplay = funnelSnapshot.map((x) => (x.stage === 'Prospección' ? { ...x, stage: '1ra Reunión' } : x));
  const funnelConversionDisplay = (data.funnel?.conversion || []).map((x) => (x.stage === 'Prospección' ? { ...x, stage: '1ra Reunión' } : x));

  const headerStatic = (data.staticKpis || []).filter((x) => (x as any).placement == null || (x as any).placement === 'header');
  const static2025 = (data.staticKpis || []).filter((x) => (x as any).placement === '2025');
  const static2026 = (data.staticKpis || []).filter((x) => (x as any).placement === '2026');

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 900 }}>Metrics Dashboard</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Updated: {new Date(data.computedAt).toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      <div className="grid kpis">
        <KpiCard title="% Win Rate (1st Meeting to Contract)" value={k.winRateP2P} format="percentage" />
        {headerStatic.map((kpi) => (
          <StaticKpiCard key={kpi.id} kpi={kpi as any} />
        ))}
      </div>

      <CacEvolutionSection />

      <RetentionSummarySection retention={data.retentionSummary} detailHref="/app/kpis/retention" canOpenDetail={Boolean(me?.company.metricsDetailAccess)} />

      {r25 ? (
        <div className="grid" style={{ gap: 14 }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>2025 Review</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  Bookings & pipeline creation (2025)
                </div>
              </div>
            </div>
            <div style={{ height: 12 }} />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
              <KpiCard title="Bookings" value={r25.bookings} format="currency" />
              <KpiCard title="Sales Cycle (days)" value={r25.cycleDays} format="days" />
              <KpiCard title="Pipeline Generation" value={(r25 as any).pipelineGeneration ?? (r25.pipelineCreationByQuarter || []).reduce((s, x) => s + (x.amount || 0), 0)} format="currency" />
            </div>
            <div style={{ height: 12 }} />
            {static2025.length ? (
              <div className="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                {static2025.map((kpi) => (
                  <StaticKpiCard key={kpi.id} kpi={kpi as any} />
                ))}
              </div>
            ) : null}
            <div style={{ height: 12 }} />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <SimpleBarChart title="Bookings by quarter (2025)" items={r25.bookingsByQuarter} valueKey="amount" labelKey="quarter" format="currency" />
              <SimpleBarChart title="Pipeline creation by quarter (2025)" items={r25.pipelineCreationByQuarter} valueKey="amount" labelKey="quarter" format="currency" />
            </div>
          </div>
        </div>
      ) : null}

      {y26 ? (
        <div className="grid" style={{ gap: 14 }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>2026 Live Data</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  As of {y26.endYmd || new Date(data.computedAt).toISOString().slice(0, 10)}
                </div>
              </div>
            </div>
            <div style={{ height: 12 }} />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
              <KpiCard title="Bookings" value={y26.bookings} format="currency" />
              <KpiCard title="Sales Cycle (days)" value={y26.cycleDays} format="days" />
              <KpiCard title="Open Pipeline" value={y26.openPipeline} format="currency" />
            </div>
            <div style={{ height: 12 }} />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
              <KpiCard title="New Clients" value={y26.clientsCount} format="number" />
              {static2026.map((kpi) => (
                <StaticKpiCard key={kpi.id} kpi={kpi as any} />
              ))}
            </div>
            <div style={{ height: 12 }} />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <SimpleBarChart title="Bookings by quarter (2026)" items={y26.bookingsByQuarter} valueKey="amount" labelKey="quarter" format="currency" />
              <SimpleBarChart title="Pipeline creation by quarter (2026)" items={y26.pipelineCreationByQuarter} valueKey="amount" labelKey="quarter" format="currency" />
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <SimpleBarChart title="Pipeline funnel ($)" items={funnelSnapshotDisplay} valueKey="amount" labelKey="stage" format="currency" />
        <SimpleBarChart title="Funnel conversion (%)" items={funnelConversionDisplay} valueKey="convFromPrev" labelKey="stage" format="percentage" />
      </div>
    </div>
  );
}
